import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ShoppingCart, Star, Users, Smartphone, ShieldCheck, Filter, ShieldAlert } from 'lucide-react';
import { productApi, settingsApi } from '@/api-client';
import type { Product } from '@/types/product';
import { getPriceStr } from '@/types/product';
import { SEO } from '@/components/SEO';

const categories = [
  { id: 'all', label: 'Alle Produkte' },
  { id: 'digital', label: 'Lifestyle-Spiele' },
  { id: 'software', label: 'Apps & Tools' },
  { id: 'gift', label: 'Besonderes' },
  { id: 'adult', label: '18+ Inhalte' },
];

interface ShopProps {
  onAddToCart: (product: Product) => void;
}

export function Shop({ onAddToCart }: ShopProps) {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [shopEnabled, setShopEnabled] = useState(true);

  // ═══ AGE GATE – sessionStorage (bei jedem neuen Besuch/Tab) ═══
  const [ageVerified, setAgeVerified] = useState(() => {
    try { return sessionStorage.getItem('jl-age-verified') === 'true'; } catch { return false; }
  });
  const [showAdultGate, setShowAdultGate] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ type: 'category' | 'product'; value: string } | null>(null);

  useEffect(() => {
    productApi.list().then(data => {
      setProducts(Array.isArray(data) ? data : []);
      setLoading(false);
    }).catch(() => setLoading(false));

    settingsApi.getShopStatus()
      .then(d => setShopEnabled(d.enabled))
      .catch(() => setShopEnabled(false));
  }, []);

  const handleCategoryClick = (catId: string) => {
    if (catId === 'adult' && !ageVerified) {
      setPendingAction({ type: 'category', value: catId });
      setShowAdultGate(true);
      return;
    }
    setActiveCategory(catId);
  };

  const handleProductClick = (product: Product) => {
    if ((product as any).isAdult && !ageVerified) {
      setPendingAction({ type: 'product', value: product.slug || String(product.id) });
      setShowAdultGate(true);
      return;
    }
    navigate(`/shop/${product.slug || product.id}`);
  };

  const confirmAge = () => {
    try { sessionStorage.setItem('jl-age-verified', 'true'); } catch {}
    setAgeVerified(true);
    setShowAdultGate(false);
    
    if (pendingAction?.type === 'category') {
      setActiveCategory(pendingAction.value);
    } else if (pendingAction?.type === 'product') {
      navigate(`/shop/${pendingAction.value}`);
    }
    setPendingAction(null);
  };

  const cancelAge = () => {
    setShowAdultGate(false);
    setPendingAction(null);
  };

  // ═══ JUGENDSCHUTZ-FILTER ═══
  const filteredProducts = (Array.isArray(products) ? products : [])
    .filter(p => {
      const isAdult = (p as any).isAdult === true;
      if (activeCategory === 'adult') return isAdult;
      if (isAdult) return false;
      if (activeCategory === 'all') return true;
      return p.category === activeCategory;
    })
    .filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                 (p.description || '').toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'price-asc') return (a.price || 0) - (b.price || 0);
      if (sortBy === 'price-desc') return (b.price || 0) - (a.price || 0);
      return 0;
    });

  return (
    <>
      <SEO 
        title="App-Shop | Lifestyle & Erotik-Spiele für Paare" 
        description="Entdecken Sie LustDuell und weitere exklusive Apps für Paare und die Swinger-Szene. Diskrete Software, sicher und ohne Abos direkt vom Entwickler."
        keywords="LustDuell kaufen, Lifestyle Apps Paare, Swinger Apps Android, Erotik Spiele Shop, Paar-Software, JL-Software Shop"
        path="/shop"
        type="website"
      />

      <div className="mt-[70px] min-h-screen bg-surface-primary">
        {/* Hero */}
        <section className="pt-16 pb-12 border-b border-white/[0.06] bg-surface-secondary/30">
          <div className="max-w-[1200px] mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="text-3xl md:text-5xl font-display font-bold italic text-glow mb-4">
                Premium <span className="text-gold">Apps & Spiele</span>
              </h1>
              <p className="text-text-secondary max-w-2xl leading-relaxed">
                Entdecken Sie unsere kuratierte Auswahl an Software-Lösungen, die speziell für Paare und anspruchsvolle Lifestyle-Nutzer entwickelt wurden.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Filter & Suche */}
        <section className="sticky top-[70px] z-30 bg-surface-primary/80 backdrop-blur-xl border-b border-white/[0.06]">
          <div className="max-w-[1200px] mx-auto px-6 py-4">
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0 w-full lg:w-auto no-scrollbar">
                <Filter size={16} className="text-gold mr-2 shrink-0" />
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      activeCategory === cat.id 
                        ? 'bg-gold text-surface-primary shadow-gold' 
                        : 'bg-white/5 text-text-secondary hover:bg-white/10'
                    } ${cat.id === 'adult' && activeCategory !== 'adult' ? 'border border-crimson/30 text-crimson hover:bg-crimson/10' : ''}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="flex gap-3 w-full lg:w-auto">
                <div className="relative flex-1 lg:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={18} />
                  <input
                    type="text"
                    placeholder="Suchen nach LustDuell, Apps..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-gold/50 focus:outline-none transition-all"
                  />
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-text-secondary focus:outline-none focus:border-gold/50 cursor-pointer"
                >
                  <option value="featured" className="bg-surface-primary">Empfohlen</option>
                  <option value="price-asc" className="bg-surface-primary">Günstigster Preis</option>
                  <option value="price-desc" className="bg-surface-primary">Höchster Preis</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* AGE GATE MODAL */}
        <AnimatePresence>
          {showAdultGate && (
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="bg-surface border border-crimson/30 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
              >
                <ShieldAlert size={48} className="text-crimson mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-text-primary mb-2">Altersbeschränkung</h2>
                <p className="text-text-secondary mb-6 text-sm leading-relaxed">
                  Dieser Bereich enthält Inhalte, die nur für Erwachsene ab 18 Jahren bestimmt sind. 
                  Mit dem Betritt bestätigen Sie, dass Sie das 18. Lebensjahr vollendet haben.
                </p>
                <div className="flex gap-3 justify-center">
                  <button 
                    onClick={cancelAge}
                    className="px-5 py-2.5 text-text-secondary font-medium rounded-xl hover:bg-white/[0.04] transition-all border border-white/[0.06]"
                  >
                    Zurück
                  </button>
                  <button 
                    onClick={confirmAge}
                    className="px-5 py-2.5 bg-crimson text-white font-bold rounded-xl hover:bg-crimson/80 transition-all"
                  >
                    Ja, ich bin über 18
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Produkte Grid */}
        <section className="max-w-[1200px] mx-auto px-6 py-12">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="animate-spin w-10 h-10 border-2 border-gold border-t-transparent rounded-full" />
              <p className="text-text-muted animate-pulse">Katalog wird geladen...</p>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <AnimatePresence mode="popLayout">
                {filteredProducts.map((product) => (
                  <motion.div
                    key={product.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ShopProductCard 
                      product={product} 
                      onAddToCart={onAddToCart} 
                      shopEnabled={shopEnabled}
                      onProductClick={handleProductClick}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="text-center py-24 border border-dashed border-white/10 rounded-3xl">
              <p className="text-text-secondary text-lg">Keine Produkte in dieser Auswahl gefunden.</p>
              <button onClick={() => {setActiveCategory('all'); setSearchQuery('');}} className="mt-4 text-gold hover:underline">Alle Produkte anzeigen</button>
            </div>
          )}
        </section>

        {/* ═══ SICHER BEZAHLEN – MIT ECHTEN LOGOS ═══ */}
        <section className="max-w-[1200px] mx-auto px-6 py-16 border-t border-white/[0.06]">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-text-primary mb-4">Warum Software von JL-Software kaufen?</h2>
              <p className="text-text-secondary leading-relaxed mb-6">
                Wir verstehen, dass Diskretion im Bereich der Lifestyle- und Erotik-Apps an erster Stelle steht. Unsere Produkte wie <strong>LustDuell</strong> werden lokal entwickelt und benötigen keine dubiosen Cloud-Anbindungen oder Abonnements.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-sm text-text-secondary">
                  <ShieldCheck size={16} className="text-gold" /> Einmaliger Kauf, lebenslange Nutzung
                </li>
                <li className="flex items-center gap-2 text-sm text-text-secondary">
                  <ShieldCheck size={16} className="text-gold" /> Höchster Datenschutz ohne Tracking
                </li>
                <li className="flex items-center gap-2 text-sm text-text-secondary">
                  <ShieldCheck size={16} className="text-gold" /> Support direkt vom Entwickler aus Deutschland
                </li>
              </ul>
            </div>
            <div className="p-8 rounded-3xl bg-gold-dim border border-gold/20">
              <h3 className="text-gold font-bold mb-2 text-lg">Sicher bezahlen</h3>
              <p className="text-text-secondary text-sm mb-6">
                Wir nutzen verschlüsselte Zahlungsmethoden und diskrete Abbuchungsbezeichnungen, damit Ihre Privatsphäre gewahrt bleibt.
              </p>
              
              {/* ═══ ZAHLUNGS-LOGOS ═══ */}
              <div className="flex gap-4 items-center flex-wrap">
                {/* PayPal */}
                <div className="h-10 px-3 bg-[#003087] rounded-lg flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                  <svg viewBox="0 0 24 24" className="h-5 w-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.5 3h9c1.66 0 3 1.34 3 3v12c0 1.66-1.34 3-3 3h-9c-1.66 0-3-1.34-3-3V6c0-1.66 1.34-3 3-3z" fill="#003087"/>
                    <path d="M8.5 8.5h3.5c.5 0 .8.5.7 1l-.4 2c-.1.4-.5.7-.9.7H9.3l-.3 1.5c0 .3-.3.5-.6.5H7.5l1-5.7z" fill="#009CDE"/>
                    <path d="M12.5 8.5H16c.5 0 .8.5.7 1l-.3 1.5c-.1.4-.5.7-.9.7h-1.5l-.3 1.5c0 .3-.3.5-.6.5h-1.3l1-5.2z" fill="#003087" fillOpacity="0.6"/>
                  </svg>
                  <span className="text-white font-bold text-[10px] ml-1 tracking-wide">PayPal</span>
                </div>

                {/* Klarna */}
                <div className="h-10 px-3 bg-[#FFB3C7] rounded-lg flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                  <svg viewBox="0 0 24 24" className="h-5 w-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="4" width="20" height="16" rx="3" fill="#FFB3C7"/>
                    <path d="M7 16V8h1.5v3l2.5-3H13l-3 3.5 3.2 4.5h-1.8L8.5 12V16H7z" fill="#0A0B09"/>
                  </svg>
                  <span className="text-[#0A0B09] font-bold text-[10px] ml-1 tracking-wide">Klarna</span>
                </div>

                {/* Kreditkarte (Visa/MC Style) */}
                <div className="h-10 px-3 bg-white/10 border border-white/20 rounded-lg flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                  <svg viewBox="0 0 24 24" className="h-5 w-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="5" width="20" height="14" rx="2" fill="white" fillOpacity="0.1" stroke="white" strokeOpacity="0.3" strokeWidth="1"/>
                    <rect x="2" y="9" width="20" height="2.5" fill="white" fillOpacity="0.3"/>
                    <circle cx="13.5" cy="14.5" r="2.5" fill="#EB001B" fillOpacity="0.9"/>
                    <circle cx="16.5" cy="14.5" r="2.5" fill="#F79E1B" fillOpacity="0.9"/>
                  </svg>
                  <span className="text-white font-bold text-[9px] ml-1 tracking-wider">VISA • MC</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

function ShopProductCard({ product, onAddToCart, shopEnabled, onProductClick }: { 
  product: Product; 
  onAddToCart: (p: Product) => void; 
  shopEnabled: boolean;
  onProductClick: (p: Product) => void;
}) {
  const isFree = product.price === 0;
  const isInquiry = product.price === -1;
  const isAdult = (product as any).isAdult === true;

  return (
    <div 
      onClick={() => onProductClick(product)}
      className="group flex flex-col h-full bg-surface border border-white/[0.06] rounded-3xl overflow-hidden hover:border-gold/30 hover:shadow-card transition-all duration-300 cursor-pointer"
    >
      <div className="relative h-56 overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-surface/80 to-transparent" />
        
        {product.badge && (
          <span className="absolute top-4 right-4 px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-gold text-surface-primary rounded-full">
            {product.badge}
          </span>
        )}
        
        {isAdult && (
          <span className="absolute top-4 left-4 px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-crimson text-white rounded-full flex items-center gap-1 shadow-lg">
            <ShieldAlert size={10} /> 18+
          </span>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-xl font-bold text-text-primary group-hover:text-gold transition-colors">
            {product.name}
          </h3>
          <div className="flex items-center gap-1 text-gold">
            <Star size={14} fill="currentColor" />
            <span className="text-xs font-bold">4.9</span>
          </div>
        </div>

        <p className="text-text-secondary text-sm line-clamp-3 mb-6 flex-1">
          {product.description}
        </p>

        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-1.5 text-xs text-text-muted">
            <Smartphone size={14} /> Android
          </div>
          <div className="flex items-center gap-1.5 text-xs text-text-muted">
            <Users size={14} /> Paar-Modus
          </div>
        </div>

        <div className="flex items-center justify-between mt-auto pt-5 border-t border-white/[0.06]">
          <div className="flex flex-col">
            {isInquiry ? (
              <span className="text-gold font-bold">Auf Anfrage</span>
            ) : (
              <span className="text-2xl font-black text-text-primary">
                {getPriceStr(product.price)} <span className="text-sm font-normal text-text-muted">€</span>
              </span>
            )}
          </div>

          {shopEnabled && !isInquiry ? (
            <button 
              onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-gold text-surface-primary rounded-xl font-bold text-sm hover:shadow-gold hover:-translate-y-0.5 transition-all"
            >
              <ShoppingCart size={16} />
              {isFree ? 'Laden' : 'Kaufen'}
            </button>
          ) : (
            <span className="text-xs text-text-muted font-medium px-3 py-2 bg-white/[0.03] rounded-lg border border-white/[0.06]">
              {shopEnabled ? 'Nur Details' : 'Browsing-Modus'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}